import React, { useState } from "react";
import { Avatar, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton, List, ListItem, ListItemAvatar, Typography } from "@mui/material";
import { dialogStyle } from "../../../shared/styles/dialogStyles";
import CloseIcon from "@mui/icons-material/Close";
import { useWindowSize } from "../../../shared/hooks/useWindowSize";
import { useTranslation } from "react-i18next";
import Rating from "../../../shared/components/Rating";
import { useLazyGetReviewsQuery } from "../../../app/store/api/workshopApi";

const Reviews = ({ workshopId }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { isMobile } = useWindowSize();
  const { t, i18n } = useTranslation();
  const [fetchReviews, { isFetching }] = useLazyGetReviewsQuery();
  const [reviews, setReviews] = useState([]);
  const fontColor = { color: "#003C59" };
  const handleDialogOpen = async (e) => {
    e.stopPropagation();
    setDialogOpen(true);

    try {
      const data = await fetchReviews({ workshopId, language: i18n.language }).unwrap();
      setReviews(data?.reviews || []);
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    }
  };

  const handleDialogClose = (e) => {
    e.stopPropagation();
    setDialogOpen(false);
  };

  return (
    <>
      <Grid container spacing={2}>
        <Button onClick={(e) => handleDialogOpen(e)} sx={{ textTransform: "none", fontSize: isMobile ? "0.75rem" : "1rem" }}>
          {t("review.showReview")}
        </Button>
      </Grid>

      <Dialog open={dialogOpen} onClose={(e) => handleDialogClose(e)} maxWidth={!isMobile ? "md" : "sm"} fullWidth sx={{ ...dialogStyle }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Typography variant='h6' sx={{ ...fontColor, fontWeight: "bold" }}>
            {t("review.title")}
          </Typography>
          <IconButton onClick={(e) => handleDialogClose(e)} sx={{ color: "#003C59" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {isFetching ? (
            <Box sx={{ minHeight: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {" "}
              <CircularProgress />{" "}
            </Box>
          ) : reviews?.length === 0 ? (
            <Box sx={{ minHeight: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {" "}
              <Typography color='text.secondary'> {t("review.noReviews")} </Typography>{" "}
            </Box>
          ) : (
            <List sx={{ width: "100%", bgcolor: "background.paper" }}>
              {reviews?.map((review) => (
                <React.Fragment key={review.name}>
                  <ListItem alignItems='flex-start' key={review.name} sx={{ display: "flex", gap: 1 }}>
                    {!isMobile && (
                      <ListItemAvatar sx={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Avatar alt={""} src='' />
                      </ListItemAvatar>
                    )}
                    <Grid item xs={12} sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexGrow: 1, justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography variant='body1' color='text.primary' sx={{ fontWeight: "bold", fontSize: !isMobile ? "1rem" : "0.875rem" }}>
                            {review?.authorAttribution?.displayName || "John Doe"}
                          </Typography>
                          <Typography variant='caption' color='text.secondary' sx={{ fontSize: !isMobile ? "0.875rem" : "0.75rem" }}>
                            {review?.relativePublishTimeDescription || "1 week ago"}
                          </Typography>
                        </Box>
                        <Box sx={{ alignItems: "center", justifyContent: "center" }}>
                          <Rating rating={{ rating: review.rating, userRatingCount: review.userRatingCount }} />
                        </Box>
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.primary"
                          lang={review?.text?.languageCode || "fi"}
                          sx={{
                            fontSize: !isMobile ? "0.875rem" : "0.75rem",
                            lineHeight: 1.5,
                            textAlign: isMobile ? "left" : "justify",
                            hyphens: "auto",
                            wordBreak: "normal",
                            overflowWrap: "break-word",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {review?.text?.text || "No review text available"}
                        </Typography>
                      </Box>
                    </Grid>
                  </ListItem>
                  <Divider variant='fullWidth' component='li' />
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Reviews;
